import type { ChatChannel } from "odyssey-shared";
import type { ChatScope } from "./scope-icon.tsx";
import { say } from "../../words.ts";

/**
 * The scope a draft speaks in, read from its first word (ADR-068's amendments "chat commands, Discord style" and
 * "Global, Trade and Help"): what the entry row's scope picker shows, and what a typed command moves.
 */

/**
 * The channel words of the commands that speak in one: `/g`, `/t` and `/h`.
 */
const CHANNEL_COMMAND = /^\/([gth])(\s|$)/;

/**
 * The channel each channel command speaks in.
 */
const COMMAND_CHANNEL: Readonly<Record<string, ChatChannel>> = { g: "global", t: "trade", h: "help" };

/**
 * The channel a draft speaks in, for `/g`, `/t` and `/h`.
 *
 * @param draft The input's text.
 * @returns The channel, or null for a draft that names none.
 */
function draftChannel(draft: string): ChatChannel | null {
  const word = CHANNEL_COMMAND.exec(draft.trimStart().toLowerCase())?.[1];
  return word === undefined ? null : (COMMAND_CHANNEL[word] ?? null);
}

/**
 * Whether a draft's first word is `/p`, which speaks to Party (ADR-101 decision 25).
 *
 * @param typed The draft, trimmed at the start and in lower case.
 * @returns True for `/p` alone or followed by white space.
 */
function isPartyCommand(typed: string): boolean {
  return /^\/p(\s|$)/.test(typed);
}

/**
 * The scope the entry row names for a draft: a whisper for `/w`, `/whisper` and a reply, Local for an action and
 * for plain text, Global, Trade or Help for `/g`, `/t` and `/h` (ADR-068's amendment "Global, Trade and Help"), Party
 * for `/p` (ADR-101 decision 25), and a command for any other slash.
 *
 * @param draft The input's text.
 * @returns The word.
 */
export function draftScope(draft: string): string {
  const typed = draft.trimStart().toLowerCase();
  if (/^\/(w|whisper|re|reply|\^)(\s|$)/.test(typed)) {
    return say("chat.scopes.whisper");
  }
  const channel = draftChannel(typed);
  if (channel !== null) {
    return say(`chat.scopes.${channel}`);
  }
  if (isPartyCommand(typed)) {
    return say("chat.scopes.party");
  }
  if (/^\/me(\s|$)/.test(typed)) {
    return say("chat.scopes.local");
  }
  return typed.startsWith("/") ? say("chat.commandScope") : say("chat.scopes.local");
}

/**
 * The scope icon the entry row shows for a draft: Whisper's for a whisper or a reply, Local's for plain text and an
 * action, Global's, Trade's or Help's for `/g`, `/t` or `/h`, Party's for `/p`, and none for a command, which is no scope.
 *
 * @param draft The input's text.
 * @returns The scope, or null for none.
 */
export function draftScopeIcon(draft: string): ChatScope | null {
  const channel = draftChannel(draft);
  if (channel !== null) {
    return channel;
  }
  if (isPartyCommand(draft.trimStart().toLowerCase())) {
    return "party";
  }
  const word = draftScope(draft);
  return word === say("chat.scopes.whisper") ? "whisper" : word === say("chat.scopes.local") ? "local" : null;
}
